import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { NavLink, Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Settings, Sun, Moon, ArrowLeft } from 'lucide-react'; // <-- ADDED ICONS

export default function DashboardLayout() {
  const { user, logout, isImpersonating, impersonatedOrg, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setSidebarOpen] = useState(true);

  // --- 1. NEW THEME TOGGLE LOGIC ---
  const [sidebarTheme, setSidebarTheme] = useState(() => {
    return localStorage.getItem('fcaio_sidebar_theme') || 'dark';
  });

  const toggleTheme = () => {
    const newTheme = sidebarTheme === 'dark' ? 'light' : 'dark';
    setSidebarTheme(newTheme);
    localStorage.setItem('fcaio_sidebar_theme', newTheme);
  };

  const isLight = sidebarTheme === 'light';

  // Dynamic Theme Classes
  const themeClasses = {
    sidebarBg: isLight ? 'bg-white border-r border-slate-200' : 'bg-slate-900 border-r border-slate-900',
    headerBorder: isLight ? 'border-slate-100' : 'border-slate-800',
    logoText: isLight ? 'text-slate-900' : 'text-white',
    menuIcon: isLight ? 'text-slate-400 hover:text-slate-900' : 'text-slate-400 hover:text-white',
    groupTitle: isLight ? 'text-slate-400 hover:text-slate-700' : 'text-slate-500 hover:text-slate-300',
    linkActive: isLight 
      ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm' 
      : 'bg-blue-600/10 text-blue-400 border border-blue-500/20',
    linkInactive: isLight 
      ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-50' 
      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50',
    adminActive: isLight 
      ? 'bg-amber-50 text-amber-600 border border-amber-200 shadow-sm' 
      : 'bg-amber-500/10 text-amber-500 border border-amber-500/20',
    adminInactive: isLight 
      ? 'text-slate-500 hover:text-amber-600 hover:bg-amber-50/50' 
      : 'text-amber-400/70 hover:text-amber-400 hover:bg-slate-800/50'
  };

  const [openGroups, setOpenGroups] = useState({
    'Main': true,
    'The Readiness Sprint': true,
    'Strategy & Reporting': false,
    'Security & Compliance': false
  });

  const toggleGroup = (groupTitle) => {
    setOpenGroups(prev => ({ ...prev, [groupTitle]: !prev[groupTitle] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonation = () => {
    stopImpersonation();
    window.location.href = '/admin'; 
  };

  const rawTier = user?.tier !== undefined ? user.tier : user?.user_metadata?.tier;
  const currentTier = (rawTier !== undefined && rawTier !== null) ? Number(rawTier) : 0;
  const isSuperAdmin = user?.is_system_admin || user?.admin_role === 'SUPER_ADMIN';

  const navGroups = [
    {
      title: "Main",
      items: [
        { path: '/dashboard', icon: '📊', label: 'Overview' },
        { path: '/progress', icon: '📈', label: 'Progress Tracker' },
        { path: '/governance-copilot', icon: '⚖️', label: 'Governance Copilot' }
      ]
    }
  ];

  if (currentTier >= 2 || isImpersonating) {
    navGroups.push(
      {
        title: "The Readiness Sprint",
        items: [
          { path: '/executive-alignment', icon: '🤝', label: 'Executive Alignment' },
          { path: '/sprint-intake', icon: '🚀', label: 'Sprint Intake' },
          { path: '/workflow-mapping', icon: '🗺️', label: 'Workflow Mapper' },
          { path: '/capability-mapper', icon: '🧠', label: 'Capability Mapper' }
        ]
      },
      {
        title: "Strategy & Reporting",
        items: [
          { path: '/llm-readiness', icon: '☁️', label: 'LLM Readiness Grader' },
          { path: '/roi-dashboard', icon: '🎯', label: 'ROI Synthesizer' },
          { path: '/roadmap', icon: '📍', label: '6-Month Roadmap' },
          { path: '/executive-readout', icon: '📄', label: 'Executive Readout' }
        ]
      },
      {
        title: "Security & Compliance",
        items: [
          { path: '/inventory', icon: '🗄️', label: 'AI Inventory' },
          { path: '/risk-analysis', icon: '🎯', label: 'Risk Analysis' },
          { path: '/secops', icon: '🛡️', label: 'SecOps Dashboard' },
          { path: '/aibom', icon: '📦', label: 'AI-BOM Registry' }
        ]
      }
    );
  }

  const isGroupActive = (items) => items.some(item => location.pathname === item.path);
  
  // --- 2. GLOBAL BACK BUTTON CHECK ---
  // If we are NOT on the overview dashboard, show the back button.
  const isDashboard = location.pathname === '/dashboard' || location.pathname === '/';

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      
      {/* GLOBAL SIDEBAR (Now dynamically styled) */}
      <aside className={`${themeClasses.sidebarBg} transition-all duration-300 flex flex-col z-20 ${isSidebarOpen ? 'w-72' : 'w-20'}`}>
        
        {/* Sidebar Header */}
        <div className={`h-16 flex items-center justify-between px-4 border-b ${themeClasses.headerBorder}`}>
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-white font-bold text-sm">FC</span>
            </div>
            {isSidebarOpen && <span className={`${themeClasses.logoText} font-bold text-lg tracking-wide`}>FCAIO</span>}
          </div>
          <button onClick={() => setSidebarOpen(!isSidebarOpen)} className={themeClasses.menuIcon}>
            ☰
          </button>
        </div>

        {/* ACCORDION NAVIGATION */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-6 custom-scrollbar">
          {navGroups.map((group, idx) => {
            const isActiveGroup = isGroupActive(group.items);
            const isOpen = openGroups[group.title] || isActiveGroup;

            return (
              <div key={idx} className="space-y-1">
                {isSidebarOpen ? (
                  <button 
                    onClick={() => toggleGroup(group.title)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${themeClasses.groupTitle}`}
                  >
                    <span>{group.title}</span>
                    {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                ) : (
                  <div className="h-4"></div>
                )}

                {(isOpen || !isSidebarOpen) && (
                  <div className="space-y-1 mt-2">
                    {group.items.map((link) => (
                      <NavLink
                        key={link.path}
                        to={link.path}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                            isActive ? themeClasses.linkActive : themeClasses.linkInactive
                          }`
                        }
                        title={!isSidebarOpen ? link.label : ""}
                      >
                        <span className="text-xl">{link.icon}</span>
                        {isSidebarOpen && <span>{link.label}</span>}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* SYSTEM ADMIN LINK */}
        {isSuperAdmin && !isImpersonating && (
          <div className={`p-4 border-t ${themeClasses.headerBorder}`}>
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all border border-transparent ${
                  isActive ? themeClasses.adminActive : themeClasses.adminInactive
                }`
              }
            >
              <Settings className="w-5 h-5" />
              {isSidebarOpen && <span>System Admin</span>}
            </NavLink>
          </div>
        )}
      </aside>

      {/* GLOBAL MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        
        {isImpersonating && (
          <div className="bg-amber-500 text-amber-950 px-8 py-2.5 flex justify-between items-center text-sm font-bold shadow-md z-50">
            <div className="flex items-center gap-2">
                <span className="text-xl">🕵️</span>
                <span>Impersonating Client: {impersonatedOrg || user?.org_name}</span>
            </div>
            <button
                onClick={handleStopImpersonation}
                className="bg-amber-950 text-amber-400 px-4 py-1.5 rounded-md hover:bg-amber-900 transition-colors shadow-sm"
            >
              End Session & Return
            </button>
          </div>
        )}

        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 shadow-sm z-10 flex-shrink-0">
          <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            Governance Platform
          </h1>
          <div className="flex items-center gap-4">
            
            {/* THEME TOGGLE BUTTON */}
            <button 
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-100"
              title={`Switch to ${isLight ? 'Dark' : 'Light'} Sidebar`}
            >
              {isLight ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>

            {!isSuperAdmin && !isImpersonating && (
              <>
                {currentTier === 0 && (
                  <Link to="/upgrade" className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-4 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-2">
                    <span>🔓</span> Upgrade to Tier 1
                  </Link>
                )}
                {currentTier === 1 && (
                  <Link to="/sprint-intake" className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-2">
                    <span>🚀</span> Request Tier 2 Sprint
                  </Link>
                )}
                {currentTier >= 2 && (
                  <button disabled className="bg-slate-200 text-slate-500 px-4 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center gap-2 cursor-not-allowed border border-slate-300">
                    <span>⚡</span> Tier 3 (Coming Soon)
                  </button>
                )}
              </>
            )}

            {isSuperAdmin ? (
              <div className="px-3 py-1 bg-purple-100 text-purple-800 border border-purple-200 rounded-full text-xs font-bold tracking-wide uppercase shadow-sm">👑 Super Admin</div>
            ) : currentTier >= 2 ? (
              <div className="px-3 py-1 bg-blue-100 text-blue-800 border border-blue-200 rounded-full text-xs font-bold tracking-wide uppercase shadow-sm">✨ Tier {currentTier} Active</div>
            ) : currentTier === 1 ? (
              <div className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold tracking-wide uppercase shadow-sm">Tier 1 Active</div>
            ) : (
              <div className="px-3 py-1 bg-slate-100 text-slate-700 border border-slate-300 rounded-full text-xs font-bold tracking-wide uppercase shadow-sm">⏱️ Free Trial</div>
            )}
            
            <div className="flex items-center gap-4 pl-4 ml-2 border-l border-slate-200 h-8">
              <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-bold text-slate-800 leading-none">{user?.full_name}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-none">{user?.org_name || 'Organization'}</p>
                </div>
                <div className="w-9 h-9 bg-slate-800 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-sm">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
              </div>
              <button onClick={handleLogout} className="flex items-center justify-center p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Sign Out">
                <span className="text-lg">🚪</span>
              </button>
            </div>
          </div>
        </header>

        {/* 🛣️ THE GLOBAL ROUTING OUTLET WITH SMART BACK BUTTON */}
        <div className="flex-1 overflow-y-auto relative flex flex-col bg-slate-50 custom-scrollbar">
          
          {/* THE GLOBAL "BACK TO DASHBOARD" INJECTION */}
          {!isDashboard && (
            <div className="px-8 pt-6 pb-2 shrink-0">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors group w-fit"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Back to Dashboard
              </button>
            </div>
          )}

          {/* PAGE CONTENT */}
          <div className="flex-1">
            <Outlet />
          </div>

        </div>

      </main>
    </div>
  );
}